import { Editor } from '@tiptap/react';
import cn from 'classnames';
import { ReactNode } from 'react';
import { Tooltip } from '../../tooltip/tooltip';
import { BoldIcon } from './icons/bold-icon';
import { BulletListIcon } from './icons/bullet-list-icon';
import { CenterIcon } from './icons/center-icon';
import { ColorIcon } from './icons/color-icon';
import { ItalicIcon } from './icons/italic-icon';
import { JustifyIcon } from './icons/justify-icon';
import { LeftIcon } from './icons/left-icon';
import { OrderListIcon } from './icons/order-list-icon';
import { RedoIcon } from './icons/redo-icon';
import { RightIcon } from './icons/right-icon';
import { StrikeIcon } from './icons/strike-icon';
import { UnderlineIcon } from './icons/underline-icon';
import { UndoIcon } from './icons/undo-icon';
import s from './toolbar.module.css';
import { useToolbar } from './use-toolbar';

const alignStyles: Record<string, { name: string; icon: ReactNode }> = {
  left: { name: 'Слева', icon: <LeftIcon /> },
  center: { name: 'По центру', icon: <CenterIcon /> },
  right: { name: 'Справа', icon: <RightIcon /> },
  justify: { name: 'По ширине', icon: <JustifyIcon /> },
};

interface Props {
  editor: Editor | null;
  isFocused: boolean;
}

export const TipTapToolbar = ({ editor, isFocused }: Props) => {
  const { color, colorStyle, setColor, setText, t, text, textStyles, align, setAlign } =
    useToolbar(editor);

  if (!editor) {
    return null;
  }

  return (
    <div className={s.component}>
      <button
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run()}
        className={cn(s.button, editor.isActive('undo') && isFocused && s.active)}
      >
        <UndoIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run()}
        className={cn(s.button, editor.isActive('redo') && isFocused && s.active)}
      >
        <RedoIcon />
      </button>
      <Tooltip
        withArrow
        wrapperClassName={s.tooltip}
        optionsWrapperClassName={s.options}
        trigger={t(isFocused ? text : textStyles[3].name)}
        triggerWrapperClassName={s.trigger}
      >
        {textStyles.map(({ fontSize, fontWeight, key, name, level }) => {
          return (
            <button
              key={name}
              onClick={() => {
                if (level) {
                  // @ts-ignore
                  editor.chain().focus().toggleHeading({ level: level }).run();
                } else {
                  editor.chain().focus().setParagraph().run();
                }
                setText(name);
              }}
              className={cn(s.option, editor.isActive(key, { level }) && isFocused && s.active)}
              style={{ fontWeight, fontSize }}
            >
              {t(name)}
            </button>
          );
        })}
      </Tooltip>
      <Tooltip
        wrapperClassName={s.tooltip}
        optionsWrapperClassName={s.options}
        withArrow
        trigger={isFocused ? alignStyles[align].icon : alignStyles.left.icon}
        triggerWrapperClassName={s.trigger}
      >
        {Object.entries(alignStyles).map(([key, { icon, name }]) => (
          <button
            key={key}
            onClick={() => {
              editor.chain().focus().setTextAlign(key).run();
              setAlign(key);
            }}
            className={cn(s.option, editor.isActive({ textAlign: key }) && isFocused && s.active)}
          >
            {icon} {t(name)}
          </button>
        ))}
      </Tooltip>
      <Tooltip
        wrapperClassName={s.tooltip}
        optionsWrapperClassName={cn(s.options, s.colors)}
        withArrow
        trigger={<ColorIcon style={{ color: isFocused ? color : colorStyle[0] }} />}
        triggerWrapperClassName={s.trigger}
      >
        {colorStyle.map((item) => (
          <ColorIcon
            key={item}
            onClick={() => {
              editor.chain().focus().setColor(item).run();
              setColor(item);
            }}
            className={cn(
              s.option,
              s.color,
              editor.isActive('textStyle', { color: item }) && isFocused && s.active,
            )}
            style={{ color: item }}
          />
        ))}
      </Tooltip>
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={cn(s.button, editor.isActive('bold') && isFocused && s.active)}
      >
        <BoldIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={cn(s.button, editor.isActive('italic') && isFocused && s.active)}
      >
        <ItalicIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={cn(s.button, editor.isActive('underline') && isFocused && s.active)}
      >
        <UnderlineIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={cn(s.button, editor.isActive('strike') && isFocused && s.active)}
      >
        <StrikeIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn(s.button, editor.isActive('bulletList') && isFocused && s.active)}
      >
        <BulletListIcon />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={cn(s.button, editor.isActive('orderedList') && isFocused && s.active)}
      >
        <OrderListIcon />
      </button>
    </div>
  );
};
